
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Calendar,
  BarChart3,
  PieChart,
  Download,
  Filter
} from 'lucide-react';

const HQMetrics = () => {
  const kpiData = [
    {
      title: "Total Revenue",
      value: "$1.2M",
      change: "+15.3%",
      trend: "up",
      period: "This Month"
    },
    {
      title: "Total Bookings",
      value: "2,847",
      change: "+12.7%",
      trend: "up", 
      period: "This Month"
    },
    {
      title: "Patient Retention",
      value: "78.5%",
      change: "+2.1%",
      trend: "up",
      period: "Last 90 Days"
    },
    {
      title: "Average Session Value",
      value: "$420",
      change: "-3.2%",
      trend: "down",
      period: "This Month"
    }
  ];

  const locationMetrics = [
    {
      name: "Austin Studio",
      revenue: 485000,
      bookings: 1240,
      retention: 82,
      growth: 18
    },
    {
      name: "Dallas Downtown", 
      revenue: 620000,
      bookings: 1607,
      retention: 75,
      growth: 22
    },
    {
      name: "Houston Medical",
      revenue: 95000,
      bookings: 280,
      retention: 68,
      growth: 45
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Metrics & KPIs
              </h1>
              <p className="mt-1 text-amber-200">
                Bookings, revenue, retention, and usage analytics per location
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" className="border-slate-600 text-slate-300">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Key Performance Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpiData.map((kpi, index) => (
              <Card key={index} className="bg-slate-800/50 backdrop-blur border-slate-600/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-slate-300">{kpi.title}</p>
                    <TrendingUp className={`h-4 w-4 ${
                      kpi.trend === 'up' ? 'text-emerald-400' : 'text-red-400'
                    }`} />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{kpi.value}</p>
                  <div className="flex items-center justify-between">
                    <Badge className={`${
                      kpi.trend === 'up' 
                        ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30'
                        : 'bg-red-500/20 text-red-200 border-red-500/30'
                    }`}>
                      {kpi.change}
                    </Badge>
                    <span className="text-xs text-slate-400">{kpi.period}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Location Performance */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-amber-400" />
                Location Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {locationMetrics.map((location, index) => (
                  <div key={index} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div>
                        <h3 className="text-white font-semibold mb-1">{location.name}</h3>
                        <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/30">
                          Active
                        </Badge>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-slate-300 text-sm">Revenue</p>
                        <p className="text-white font-semibold">${location.revenue.toLocaleString()}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-slate-300 text-sm">Bookings</p>
                        <p className="text-white font-semibold">{location.bookings.toLocaleString()}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-slate-300 text-sm">Retention</p>
                        <p className="text-white font-semibold">{location.retention}%</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-slate-300 text-sm">Growth</p>
                        <p className="text-emerald-400 font-semibold">+{location.growth}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Charts and Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <PieChart className="w-5 h-5 mr-2 text-blue-400" />
                  Revenue by Treatment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { treatment: "Botox", percentage: 35, amount: "$420K" },
                    { treatment: "Dermal Fillers", percentage: 28, amount: "$336K" },
                    { treatment: "Skin Boosters", percentage: 20, amount: "$240K" },
                    { treatment: "Laser Treatments", percentage: 17, amount: "$204K" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span className="text-slate-300">{item.treatment}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold">{item.amount}</p>
                        <p className="text-slate-400 text-sm">{item.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-purple-400" />
                  Monthly Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Peak Booking Hours</p>
                    <p className="text-white font-semibold">2:00 PM - 4:00 PM</p>
                    <p className="text-slate-400 text-sm">38% of daily bookings</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Average Wait Time</p>
                    <p className="text-white font-semibold">12 days</p>
                    <p className="text-emerald-400 text-sm">-2 days from last month</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-300 text-sm mb-2">Customer Satisfaction</p>
                    <p className="text-white font-semibold">4.8/5.0</p>
                    <p className="text-emerald-400 text-sm">+0.2 from last month</p>
                  </div>
                  
                  <div>
                    <p className="text-slate-300 text-sm mb-2">No-Show Rate</p>
                    <p className="text-white font-semibold">3.2%</p>
                    <p className="text-red-400 text-sm">+0.5% from last month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQMetrics;
